// Contains all API requests related to users under current advertiser
import axios from "./axios"
import { handleResponse } from "./util";

interface ManageUsers {
  email: string,
  name: string,
  phone_number: number,
  belongs_to_all_siblings: boolean,
  id: number,
  is_current_user: boolean,
  page: number
}

interface AdvertiserUser {
  advertiserID: number,
  phoneNumber: number,
  email: string,
  name: string,
  belongsToAllSiblings: boolean,
  userID: number,
  inventoryPermission: boolean,
  sponsorshipPermission: boolean
}

type CreateAdvertiserUser = Omit<AdvertiserUser, 'userID'>
type UpdateAdvertiserUser = Omit<AdvertiserUser, 'email'>

export const advertiserUserAPI = {
  /**
   * GET request to retrieve all users under current advertiser
   * @param {number} advertiserID - current advertiser ID
   * @param {number} page - current page number
   * @returns {Array} of objects, each object represent a user with permissions
   */
   getAllAdvertiserUsers(advertiserID: number, page: number = 1): Promise<ManageUsers[]>{
    return axios.get(`/brands/${advertiserID}/users_advertisers`, {
      params: {
        page
      }
    }).then(handleResponse)
  },
  /**
   * GET request to retrieve a certain user under current advertiser
   * @param {number} advertiserID - current advertiser ID
   * @param {number} userID - certain user ID
   * @returns {object} with certain user data (email, phone, name, permissions)
   */
  getAdvertiserUser(advertiserID: number, userID: number): Promise<ManageUsers[]>{
    return axios.get(`/brands/${advertiserID}/users_advertisers/${userID}`)
    .then(handleResponse)
  },
  /**
   * POST request to create new user under current advertiser
   * @param {CreateAdvertiserUser} params - contains new user data (name, email, phone, permissions)
   * @returns {object} with certain user data (email, phone, name, permissions)
   */
  createAdvertiserUser(params: CreateAdvertiserUser): Promise<ManageUsers[]>{
    const apiParams = {
      email: params.email,
      all_siblings: params.belongsToAllSiblings,
      receive_inventory_request_email: params.inventoryPermission,
      receive_sponsor_request_email: params.sponsorshipPermission,
      user_attributes: {
        name: params.name,
        phone_number: params.phoneNumber,
        email: params.email
      }
    }
    return axios.post(`/brands/${params.advertiserID}/users_advertisers`, apiParams)
      .then(handleResponse)
  },
  /**
   * PATCH request to update existing user under current advertiser with new info
   * @param {UpdateAdvertiserUser} params - contains existing user changed data (name, phone, permissions)
   * @returns {object} with certain user data (email, phone, name, permissions)
   */
  updateAdvertiserUser(params: UpdateAdvertiserUser): Promise<ManageUsers[]>{
    const apiParams = {
      brand_id: params.advertiserID,
      all_siblings: params.belongsToAllSiblings,
      receive_inventory_request_email: params.inventoryPermission,
      receive_sponsor_request_email: params.sponsorshipPermission,
      user_attributes: {
        id: params.userID,
        phone_number: params.phoneNumber,
        name: params.name
      }
    }
    return axios.patch(`/brands/${params.advertiserID}/users_advertisers/${params.userID}`, apiParams)
      .then(handleResponse)
  },
  /**
   * DELETE request to remove existing under current advertiser
   * does not use handleResponse because BE doesn't return a response
   * @param {number} advertiserID - current advertiser ID
   * @param {number} userID - existing user ID
   */
  deleteAdvertiserUsers(advertiserID: number, userID: number): Promise<ManageUsers[]>{
    return axios.delete(`/brands/${advertiserID}/users_advertisers/${userID}`)
  }
}